import React from "react";
import { Platform, StyleSheet, Text, View } from "react-native";

import { AddItemForm } from "./AddItemForm";
import { EmptyItemsState } from "./EmptyItemsState";
import { FooterNav } from "./FooterNav";
import ItemsListDraggable from "./ItemsListDraggable";
import { StepCard } from "./StepCard";

import type { BusinessItem } from "../types";

type Props = {
  businessId: string | null;
  loading: boolean;
  saving: boolean;
  submitError?: string | null;
  ordered: BusinessItem[];
  canGoNext: boolean;
  showItemsError: boolean;

  onAdd: (item: BusinessItem) => void;
  editingItem: BusinessItem | null;
  onUpdate: (item: BusinessItem) => void;
  onCancelEdit: () => void;

  onReorder: (next: BusinessItem[]) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;

  onBack: () => void;
  onNext: () => void;
};

export default function ItemsWebLayout({
  businessId,
  loading,
  saving,
  submitError,
  ordered,
  canGoNext,
  showItemsError,
  onAdd,
  editingItem,
  onUpdate,
  onCancelEdit,
  onReorder,
  onDelete,
  onEdit,
  onBack,
  onNext,
}: Props) {
  return (
    <View style={styles.page}>
      <View style={styles.surface}>
        <View style={styles.content}>
          <View style={styles.header}>
            <Text style={styles.title}>Products & Services</Text>
            <Text style={styles.subtitle}>
              Add, edit, and arrange the products or services shown on your card.
            </Text>
          </View>

          {showItemsError ? (
            <View style={styles.topErrorBox}>
              <Text style={styles.topErrorText}>
                Please fix the highlighted fields before continuing.
              </Text>
            </View>
          ) : null}

          <View style={styles.grid}>
            <View style={styles.left}>
              <View style={styles.cardSurface}>
                <StepCard>
                  <Text style={styles.cardTitle}>
                    {editingItem ? "Edit item" : "Add item"}
                  </Text>

                  <View style={styles.formWrap}>
                    <AddItemForm
                      businessId={businessId}
                      onAdd={onAdd}
                      editingItem={editingItem}
                      onUpdate={onUpdate}
                      onCancelEdit={onCancelEdit}
                    />
                  </View>
                </StepCard>
              </View>

              {(!canGoNext || showItemsError) && (
                <Text
                  style={[
                    styles.helperText,
                    showItemsError ? styles.helperTextError : null,
                  ]}
                >
                  Add at least one product or service to continue.
                </Text>
              )}
            </View>

            <View style={styles.right}>
              <View
                style={[
                  styles.cardSurface,
                  showItemsError ? styles.cardSurfaceError : null,
                ]}
              >
                <StepCard>
                  {!loading && ordered.length === 0 ? (
                    <View>
                      <View style={styles.listHeaderRow}>
                        <Text style={styles.cardTitle}>Items (0)</Text>
                      </View>

                      <EmptyItemsState />
                    </View>
                  ) : (
                    <View style={styles.listSurface}>
                      <View style={styles.listArea}>
                        <ItemsListDraggable
                          items={ordered}
                          onReorder={onReorder}
                          onDelete={onDelete}
                          onEdit={onEdit}
                        />
                      </View>
                    </View>
                  )}
                </StepCard>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <View style={styles.footerInner}>
            {submitError ? (
              <Text style={styles.footerErrorText}>{submitError}</Text>
            ) : null}

            <FooterNav
              onBack={onBack}
              onNext={onNext}
              nextDisabled={loading || saving}
              nextLabel={saving ? "Saving..." : "Next: Review ->"}
              variant="light"
              center
              maxWidth={460}
              size="compact"
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    padding: 24,
    backgroundColor: "#0B0F1A",
  },

  surface: {
    flex: 1,
    backgroundColor: "#F8FAFC",
    borderRadius: 18,
    padding: 24,
  },

  content: {
    flex: 1,
    minHeight: 0,
    ...(Platform.OS === "web"
      ? ({ overflow: "auto" } as any)
      : null),
  },

  header: {
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    marginBottom: 18,
  },

  title: {
    fontSize: 24,
    fontWeight: "800",
    color: "#0F172A",
  },

  subtitle: {
    marginTop: 6,
    fontSize: 14,
    lineHeight: 20,
    color: "#64748B",
    fontWeight: "500",
  },

  topErrorBox: {
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#FCA5A5",
    backgroundColor: "#FEF2F2",
    padding: 12,
    marginBottom: 14,
  },

  topErrorText: {
    color: "#991B1B",
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "800",
  },

  grid: {
    flex: 1,
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
    flexDirection: "row",
    gap: 24,
    alignItems: "stretch",
  },

  left: {
    flex: 4,
    maxWidth: 520,
  },

  right: {
    flex: 6,
    minWidth: 480,
    minHeight: 0,
  },

  cardSurface: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 4,
    borderWidth: 1,
    borderColor: "#FFFFFF",
    ...(Platform.OS === "web"
      ? ({ boxShadow: "0 8px 24px rgba(0,0,0,0.08)" } as any)
      : null),
  },

  cardSurfaceError: {
    borderColor: "#EF4444",
  },

  cardTitle: {
    fontSize: 16,
    fontWeight: "800",
    color: "#0F172A",
  },

  formWrap: {
    marginTop: 10,
  },

  helperText: {
    marginTop: 10,
    fontSize: 13,
    lineHeight: 18,
    color: "#4B5563",
    fontWeight: "500",
    textAlign: "center",
  },

  helperTextError: {
    color: "#B91C1C",
    fontWeight: "800",
  },

  listHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    gap: 12,
  },

  listSurface: {
    backgroundColor: "#F1F5F9",
    borderRadius: 12,
    padding: 8,
    minHeight: 220,
  },

  listArea: {
    maxHeight: "70vh" as any,
    overflow: "auto" as any,
  },

  footer: {
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#E5E7EB",
  },

  footerInner: {
    backgroundColor: "#FFFFFF",
    borderRadius: 14,
    padding: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    ...(Platform.OS === "web"
      ? ({ boxShadow: "0 8px 20px rgba(0,0,0,0.08)" } as any)
      : null),
  },

  footerErrorText: {
    marginBottom: 10,
    textAlign: "center",
    fontSize: 13,
    lineHeight: 18,
    color: "#B91C1C",
    fontWeight: "800",
  },
});
